import React, { useState, useRef } from "react";
import Grid from "@mui/material/Unstable_Grid2";
import {
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  Divider,
  IconButton,
  Chip,
  alpha,
  useTheme,
  Fade,
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Tab,
  Tabs,
  Tooltip,
  LinearProgress,
  Collapse,
  Card,
  CardContent,
} from "@mui/material";
import {
  AutoStories,
  Lightbulb,
  FormatListBulleted,
  Palette,
  DesignServices,
  Close,
  Download,
  ContentCopy,
  Share,
  CheckCircle,
  MenuBook,
  Star,
  Bookmark,
  BookmarkBorder,
  ExpandMore,
  ExpandLess,
  LocalLibrary,
  Language,
  Print,
  Fullscreen,
  FullscreenExit,
  AutoAwesome,
  Timeline,
  Image,
  Summarize,
  ViewList,
  ArrowForwardIos,
  PictureAsPdf,
  EmojiEvents,
} from "@mui/icons-material";
 import { withStyles } from "@material-ui/core/styles";

const LightTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: "blue",
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}))(Tooltip);

// ─── Section collapse wrapper ────────────────────────────────
const BookSection = ({ id, label, icon, color, gradient, content, isActive, onToggle, index, formattedContent }) => {
  const theme = useTheme();
 
  return (
    <Fade in timeout={300 + index * 120}>
      <Paper
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          border: isActive
            ? `2px solid ${color}`
            : `1px solid ${alpha(theme.palette.divider, 0.15)}`,
          boxShadow: isActive
            ? `0 8px 32px ${alpha(color, 0.18)}`
            : "0 2px 12px rgba(0,0,0,0.05)",
          transition: "all 0.35s cubic-bezier(0.4,0,0.2,1)",
          "&:hover": {
            boxShadow: `0 8px 28px ${alpha(color, 0.2)}`,
            borderColor: alpha(color, 0.5),
          },
        }}
      >
        {/* Section Header */}
        <Box
          onClick={onToggle}
          sx={{
            cursor: "pointer",
            px: 3,
            py: 2,
            background: isActive
              ? `linear-gradient(135deg, ${alpha(color, 0.12)}, ${alpha(color, 0.05)})`
              : theme.palette.mode === "dark"
              ? alpha("#fff", 0.03)
              : alpha("#000", 0.02),
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: isActive ? `1px solid ${alpha(color, 0.15)}` : "none",
            transition: "all 0.2s ease",
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            {/* Step badge */}
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                background: gradient,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                flexShrink: 0,
                boxShadow: `0 4px 14px ${alpha(color, 0.35)}`,
              }}
            >
              {icon}
            </Box>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{ color: isActive ? color : "text.primary" }}
                >
                  {label}
                </Typography>
                {content && (
                  <Chip
                    icon={<CheckCircle sx={{ fontSize: "13px !important", color: "#44AF69 !important" }} />}
                    label="Ready"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      bgcolor: alpha("#44AF69", 0.12),
                      color: "#44AF69",
                      border: `1px solid ${alpha("#44AF69", 0.3)}`,
                    }}
                  />
                )}
                {!content && (
                  <Chip
                    label="Pending"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.65rem",
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                      color: theme.palette.warning.main,
                    }}
                  />
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {content
                  ? `${content.length} characters · Click to ${isActive ? "collapse" : "expand"}`
                  : "Not generated yet"}
              </Typography>
            </Box>
          </Stack>
 
          <IconButton
            size="small"
            sx={{
              color: color,
              background: alpha(color, 0.08),
              "&:hover": { background: alpha(color, 0.16) },
              transition: "transform 0.3s ease",
              transform: isActive ? "rotate(0deg)" : "rotate(-90deg)",
            }}
          >
            {isActive ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
 
        {/* Section Content */}
        <Collapse in={isActive} timeout={320}>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 2.5,
              background:
                theme.palette.mode === "dark"
                  ? alpha("#0f172a", 0.8)
                  : alpha("#ffffff", 0.9),
            }}
          >
            {content ? (
              <Box
                sx={{
                  fontFamily: "'Georgia', 'Times New Roman', serif",
                  fontSize: "0.95rem",
                  lineHeight: 1.85,
                  color: "text.primary",
                  whiteSpace: "pre-wrap",
                  maxHeight: 360,
                  overflow: "auto",
                  pr: 1,
                  "&::-webkit-scrollbar": { width: 4 },
                  "&::-webkit-scrollbar-thumb": {
                    borderRadius: 4,
                    background: alpha(color, 0.3),
                  },
                }}
              >
                {content}
              </Box>
            ) : (
              <Box
                sx={{
                  textAlign: "center",
                  py: 4,
                  color: "text.disabled",
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    background: alpha(color, 0.08),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 1.5,
                    color: alpha(color, 0.4),
                  }}
                >
                  {icon}
                </Box>
                <Typography variant="body2">
                  Go back and generate this section to fill it in.
                </Typography>
              </Box>
            )}
          </Box>
        </Collapse>
      </Paper>
    </Fade>
  );
};
 
// ─── Book Spine Cover Card ────────────────────────────────────
const BookSpineCard = ({ generators, pipelineContents, bookType, language, completedCount, total }) => {
  const theme = useTheme();
  const primaryColor = generators[0]?.color || "#4A90E2";
 
  return (
    <Paper
      sx={{
        borderRadius: 4,
        overflow: "hidden",
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(160deg, #1e293b 0%, #0f172a 100%)"
            : "linear-gradient(160deg, #ffffff 0%, #f1f5f9 100%)",
        border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        boxShadow: "0 16px 48px rgba(0,0,0,0.1)",
        position: "relative",
        height: "100%",
        minHeight: 400,
      }}
    >
      {/* Decorative spine */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 8,
          background: "linear-gradient(180deg, #FF6B35, #4A90E2, #44AF69, #9C27B0)",
          borderRadius: "4px 0 0 4px",
        }}
      />
 
      <Box sx={{ p: 3, pl: 4 }}>
        {/* Book icon + badge */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 3,
              background: "linear-gradient(135deg, #4A90E2, #9C27B0)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 8px 24px rgba(74,144,226,0.35)",
            }}
          >
            <MenuBook sx={{ color: "white", fontSize: 32 }} />
          </Box>
          <Chip
            icon={<EmojiEvents sx={{ fontSize: "15px !important" }} />}
            label={`${completedCount}/${total} Complete`}
            sx={{
              fontWeight: 700,
              fontSize: "0.75rem",
              background:
                completedCount === total
                  ? "linear-gradient(135deg, #44AF69, #2E8B57)"
                  : "linear-gradient(135deg, #4A90E2, #9C27B0)",
              color: "white",
              boxShadow:
                completedCount === total
                  ? "0 3px 10px rgba(68,175,105,0.4)"
                  : "0 3px 10px rgba(74,144,226,0.3)",
            }}
          />
        </Stack>
 
        {/* Title area */}
        <Typography
          variant="h5"
          fontWeight={800}
          sx={{
            mb: 1,
            background: "linear-gradient(135deg, #4A90E2, #9C27B0)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            lineHeight: 1.2,
          }}
        >
          Your Complete Book
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          All stages compiled into one complete view
        </Typography>
 
        <Divider sx={{ mb: 2.5, opacity: 0.4 }} />
 
        {/* Metadata grid */}
        <Stack spacing={1.5}>
          {[
            { label: "Genre", value: bookType || "—", icon: <LocalLibrary sx={{ fontSize: 16 }} /> },
            { label: "Language", value: language || "English", icon: <Language sx={{ fontSize: 16 }} /> },
            { label: "Stages", value: `${completedCount} of ${total}`, icon: <Timeline sx={{ fontSize: 16 }} /> },
          ].map((item) => (
            <Stack
              key={item.label}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ color: "text.disabled" }}>{item.icon}</Box>
                <Typography variant="caption" color="text.secondary">
                  {item.label}
                </Typography>
              </Stack>
              <Typography
                variant="caption"
                fontWeight={600}
                sx={{ textTransform: "capitalize" }}
              >
                {item.value}
              </Typography>
            </Stack>
          ))}
        </Stack>
 
        <Divider sx={{ my: 2.5, opacity: 0.4 }} />
 
        {/* Mini stage progress pills */}
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1.5, display: "block" }}>
          PIPELINE STATUS
        </Typography>
        <Stack spacing={1}>
          {generators.map((gen) => (
            <Stack key={gen.id} direction="row" alignItems="center" spacing={1.5}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: pipelineContents[gen.id] ? gen.gradient : alpha(theme.palette.divider, 0.5),
                  flexShrink: 0,
                  boxShadow: pipelineContents[gen.id] ? `0 0 6px ${gen.color}` : "none",
                }}
              />
              <Box sx={{ flex: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={pipelineContents[gen.id] ? 100 : 0}
                  sx={{
                    height: 4,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.divider, 0.15),
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 2,
                      background: gen.gradient,
                    },
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: pipelineContents[gen.id] ? gen.color : "text.disabled",
                  fontWeight: 600,
                  fontSize: "0.65rem",
                  minWidth: 52,
                  textAlign: "right",
                }}
              >
                {gen.label}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Box>
    </Paper>
  );
};
 
// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT: FullBookScenario
// ═══════════════════════════════════════════════════════════
const FullBookScenario = ({
  open,
  onClose,
  pipelineContents = {},
  generators = [],
  bookType = "",
  language = "English",
}) => {
  const theme = useTheme();
  const [activeSection, setActiveSection] = useState("idea");
  const [bookmarked, setBookmarked] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const contentRef = useRef(null);
 
  const completedCount = Object.values(pipelineContents).filter(Boolean).length;
  const total = generators.length;
 
  // Full merged text for export
  const fullBookText = generators
    .map((gen) =>
      pipelineContents[gen.id]
        ? `═══ ${gen.label.toUpperCase()} ═══\n\n${pipelineContents[gen.id]}\n\n`
        : ""
    )
    .filter(Boolean)
    .join("\n");
 
  const handleCopyAll = () => {
    navigator.clipboard.writeText(fullBookText).catch(() => {});
  };
 
  const handleDownloadAll = () => {
    const el = document.createElement("a");
    const file = new Blob([fullBookText], { type: "text/plain" });
    el.href = URL.createObjectURL(file);
    el.download = `full-book-scenario-${Date.now()}.txt`;
    document.body.appendChild(el);
    el.click();
    document.body.removeChild(el);
  };
 
  const sectionMeta = {
    idea: {
      icon: <Lightbulb />,
      label: "Book Ideas",
      color: "#FF6B35",
      gradient: "linear-gradient(135deg, #FF6B35, #FFA62E)",
    },
    outline: {
      icon: <FormatListBulleted />,
      label: "Book Outline",
      color: "#4A90E2",
      gradient: "linear-gradient(135deg, #4A90E2, #357ABD)",
    },
    cover: {
      icon: <Palette />,
      label: "Cover Ideas",
      color: "#44AF69",
      gradient: "linear-gradient(135deg, #44AF69, #2E8B57)",
    },
    summary: {
      icon: <DesignServices />,
      label: "Book Summary",
      color: "#9C27B0",
      gradient: "linear-gradient(135deg, #9C27B0, #7B1FA2)",
    },
  };
 
  const pipelineOrder = ["idea", "outline", "cover", "summary"];
 
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      fullScreen={fullscreen}
      TransitionComponent={Slide}
      TransitionProps={{ direction: "up" }}
      PaperProps={{
        sx: {
          borderRadius: fullscreen ? 0 : 4,
          overflow: "hidden",
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, #0f172a, #1e293b)"
              : "linear-gradient(135deg, #f8fafc, #ffffff)",
          border: fullscreen
            ? "none"
            : `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          boxShadow: "0 32px 80px rgba(0,0,0,0.25)",
          maxHeight: fullscreen ? "100vh" : "92vh",
        },
      }}
    >
      {/* ── HEADER ─────────────────────────────────────────── */}
      <Box
        sx={{
          px: { xs: 2.5, md: 4 },
          py: 2.5,
          background: "linear-gradient(135deg, #4A90E2 0%, #9C27B0 50%, #FF6B35 100%)",
          position: "relative",
          overflow: "hidden",
          "&:before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(ellipse at 20% 50%, rgba(255,255,255,0.08) 0%, transparent 60%)",
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2.5,
                background: "rgba(255,255,255,0.18)",
                backdropFilter: "blur(8px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid rgba(255,255,255,0.25)",
              }}
            >
              <AutoStories sx={{ color: "white", fontSize: 24 }} />
            </Box>
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography variant="h6" fontWeight={800} sx={{ color: "white" }}>
                  Full Book Scenario
                </Typography>
                <Chip
                  icon={<AutoAwesome sx={{ fontSize: "13px !important", color: "white !important" }} />}
                  label="AI Compiled"
                  size="small"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.2)",
                    color: "white",
                    fontWeight: 700,
                    fontSize: "0.65rem",
                    border: "1px solid rgba(255,255,255,0.3)",
                  }}
                />
              </Stack>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.75)" }}
              >
                {completedCount} of {total} stages complete ·{" "}
                {bookType && `${bookType} · `}
                {language}
              </Typography>
            </Box>
          </Stack>
 
          {/* Header Actions */}
          <Stack direction="row" spacing={1} alignItems="center">
            <LightTooltip title={bookmarked ? "Remove Bookmark" : "Bookmark"}>
              <IconButton
                onClick={() => setBookmarked(!bookmarked)}
                size="small"
                sx={{
                  color: "white",
                  background: "rgba(255,255,255,0.12)",
                  "&:hover": { background: "rgba(255,255,255,0.22)" },
                }}
              >
                {bookmarked ? (
                  <Bookmark fontSize="small" />
                ) : (
                  <BookmarkBorder fontSize="small" />
                )}
              </IconButton>
            </LightTooltip>
            <LightTooltip title={fullscreen ? "Exit Fullscreen" : "Fullscreen"}>
              <IconButton
                onClick={() => setFullscreen(!fullscreen)}
                size="small"
                sx={{
                  color: "white",
                  background: "rgba(255,255,255,0.12)",
                  "&:hover": { background: "rgba(255,255,255,0.22)" },
                }}
              >
                {fullscreen ? (
                  <FullscreenExit fontSize="small" />
                ) : (
                  <Fullscreen fontSize="small" />
                )}
              </IconButton>
            </LightTooltip>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: "white",
                background: "rgba(255,255,255,0.12)",
                "&:hover": { background: "rgba(255,255,255,0.22)" },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
 
        {/* Progress Bar */}
        <Box sx={{ mt: 2 }}>
          <LinearProgress
            variant="determinate"
            value={(completedCount / total) * 100}
            sx={{
              height: 5,
              borderRadius: 3,
              bgcolor: "rgba(255,255,255,0.2)",
              "& .MuiLinearProgress-bar": {
                borderRadius: 3,
                background: "linear-gradient(90deg, #fff, rgba(255,255,255,0.7))",
              },
            }}
          />
        </Box>
      </Box>
 
      {/* ── BODY ───────────────────────────────────────────── */}
      <DialogContent
        ref={contentRef}
        sx={{
          p: { xs: 2, md: 3 },
          overflow: "auto",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            borderRadius: 4,
            background: alpha(theme.palette.primary.main, 0.25),
          },
        }}
      >
        <Grid container spacing={3}>
          {/* Left: Book Card (sticky) */}
          <Grid xs={12} md={3.5} lg={3}>
            <Box sx={{ position: { md: "sticky" }, top: 0 }}>
              <BookSpineCard
                generators={generators}
                pipelineContents={pipelineContents}
                bookType={bookType}
                language={language}
                completedCount={completedCount}
                total={total}
              />
 
              {/* Quick nav pills */}
              <Paper
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                  sx={{ mb: 1.5, display: "block" }}
                >
                  JUMP TO SECTION
                </Typography>
                <Stack spacing={0.75}>
                  {pipelineOrder.map((id) => {
                    const meta = sectionMeta[id];
                    const done = !!pipelineContents[id];
                    return (
                      <Button
                        key={id}
                        variant={activeSection === id ? "contained" : "text"}
                        size="small"
                        startIcon={meta.icon}
                        endIcon={done && <CheckCircle sx={{ fontSize: "13px !important" }} />}
                        onClick={() => setActiveSection(id)}
                        sx={{
                          borderRadius: 2,
                          justifyContent: "flex-start",
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: "0.78rem",
                          py: 0.8,
                          ...(activeSection === id
                            ? {
                                background: meta.gradient,
                                color: "white",
                                boxShadow: `0 3px 12px ${alpha(meta.color, 0.35)}`,
                              }
                            : {
                                color: done ? meta.color : "text.disabled",
                                "&:hover": {
                                  background: alpha(meta.color, 0.08),
                                  color: meta.color,
                                },
                              }),
                        }}
                      >
                        {meta.label}
                      </Button>
                    );
                  })}
                </Stack>
              </Paper>
            </Box>
          </Grid>
 
          {/* Right: Sections */}
          <Grid xs={12} md={8.5} lg={9}>
            {/* ── Highlight Summary Banner (if both idea + summary exist) */}
            {pipelineContents.idea && pipelineContents.summary && (
              <Fade in timeout={400}>
                <Paper
                  sx={{
                    mb: 3,
                    p: 3,
                    borderRadius: 3,
                    background:
                      theme.palette.mode === "dark"
                        ? "linear-gradient(135deg, rgba(74,144,226,0.12), rgba(156,39,176,0.12))"
                        : "linear-gradient(135deg, rgba(74,144,226,0.07), rgba(156,39,176,0.07))",
                    border: `1px solid ${alpha("#4A90E2", 0.2)}`,
                    position: "relative",
                    overflow: "hidden",
                    "&:before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      background:
                        "linear-gradient(90deg, #FF6B35, #4A90E2, #44AF69, #9C27B0)",
                    },
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        background:
                          "linear-gradient(135deg, #4A90E2, #9C27B0)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Star sx={{ color: "white", fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                        Book at a Glance
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          lineHeight: 1.7,
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {pipelineContents.summary}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Fade>
            )}
 
            {/* ── Individual Sections ── */}
            <Stack spacing={2}>
              {pipelineOrder.map((id, index) => (
                <BookSection
                  key={id}
                  id={id}
                  label={sectionMeta[id].label}
                  icon={sectionMeta[id].icon}
                  color={sectionMeta[id].color}
                  gradient={sectionMeta[id].gradient}
                  content={pipelineContents[id]}
                  isActive={activeSection === id}
                  onToggle={() =>
                    setActiveSection(activeSection === id ? null : id)
                  }
                  index={index}
                />
              ))}
            </Stack>
 
            {/* ── Full Merged Preview Card ── */}
            {completedCount >= 2 && (
              <Fade in timeout={600}>
                <Paper
                  sx={{
                    mt: 3,
                    borderRadius: 3,
                    overflow: "hidden",
                    border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                    boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
                  }}
                >
                  <Box
                    sx={{
                      px: 3,
                      py: 2,
                      background:
                        theme.palette.mode === "dark"
                          ? alpha("#fff", 0.04)
                          : alpha("#000", 0.025),
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <AutoStories
                        sx={{ color: "primary.main", fontSize: 20 }}
                      />
                      <Typography variant="subtitle1" fontWeight={700}>
                        Full Compiled Book
                      </Typography>
                      <Chip
                        label={`${completedCount} Sections`}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.65rem",
                          fontWeight: 700,
                          bgcolor: alpha(
                            theme.palette.primary.main,
                            0.1
                          ),
                          color: "primary.main",
                        }}
                      />
                    </Stack>
                    <LightTooltip title="Copy All">
                      <IconButton
                        size="small"
                        onClick={handleCopyAll}
                        sx={{
                          background: alpha(
                            theme.palette.primary.main,
                            0.08
                          ),
                          "&:hover": {
                            background: alpha(
                              theme.palette.primary.main,
                              0.16
                            ),
                          },
                        }}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </LightTooltip>
                  </Box>
 
                  <Box
                    sx={{
                      px: { xs: 2, md: 3 },
                      py: 2.5,
                      maxHeight: 340,
                      overflow: "auto",
                      fontFamily: "'Georgia', 'Times New Roman', serif",
                      fontSize: "0.9rem",
                      lineHeight: 1.9,
                      color: "text.secondary",
                      whiteSpace: "pre-wrap",
                      "&::-webkit-scrollbar": { width: 4 },
                      "&::-webkit-scrollbar-thumb": {
                        borderRadius: 4,
                        background: alpha(
                          theme.palette.primary.main,
                          0.25
                        ),
                      },
                    }}
                  >
                    {fullBookText}
                  </Box>
                </Paper>
              </Fade>
            )}
          </Grid>
        </Grid>
      </DialogContent>
 
      {/* ── FOOTER ACTIONS ─────────────────────────────────── */}
      <DialogActions
        sx={{
          px: { xs: 2.5, md: 4 },
          py: 2.5,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          background:
            theme.palette.mode === "dark"
              ? alpha("#0f172a", 0.8)
              : alpha("#f8fafc", 0.8),
          backdropFilter: "blur(8px)",
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        {/* Left info */}
        <Box sx={{ flex: 1, display: { xs: "none", sm: "block" } }}>
          <Typography variant="caption" color="text.secondary">
            {completedCount === total
              ? "✅ All stages complete — ready to export!"
              : `${total - completedCount} stage(s) still pending`}
          </Typography>
        </Box>
 
        {/* Action Buttons */}
        <Stack direction="row" spacing={1.5} flexWrap="wrap" gap={1}>
          <Button
            variant="outlined"
            startIcon={<ContentCopy />}
            onClick={handleCopyAll}
            disabled={completedCount === 0}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.82rem",
            }}
          >
            Copy All
          </Button>
 
          <Button
            variant="outlined"
            startIcon={<Share />}
            disabled={completedCount === 0}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.82rem",
            }}
          >
            Share
          </Button>
 
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleDownloadAll}
            disabled={completedCount === 0}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.82rem",
              background: "linear-gradient(135deg, #4A90E2, #357ABD)",
              boxShadow: "0 4px 14px rgba(74,144,226,0.3)",
              "&:hover": {
                boxShadow: "0 6px 20px rgba(74,144,226,0.45)",
                background: "linear-gradient(135deg, #357ABD, #2C6CB0)",
              },
            }}
          >
            Download Book
          </Button>
 
         
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
 
export default FullBookScenario;


